import type { IdentifiableTItem, ItemDetails } from '@/components/views/published/types';
import type { ItemDetailsSchema, PublishedItemDetailsTabAdapter } from '../types';

interface CreatePublishedItemDetailsAdapterOptions {
  itemDetails: ItemDetails;
  enrichedItem: IdentifiableTItem; // Provides enriched display data: type, status, team, parent info
}

export const createPublishedItemDetailsAdapter = ({
  itemDetails,
  enrichedItem,
}: CreatePublishedItemDetailsAdapterOptions): PublishedItemDetailsTabAdapter => {
  const getFieldValue = <K extends keyof ItemDetailsSchema>(field: K): ItemDetailsSchema[K] => {
    switch (field) {
      case 'name':
        return itemDetails.name as ItemDetailsSchema[K];
      case 'itemStatusId':
        return itemDetails.statusId as ItemDetailsSchema[K];
      case 'itemTypeId':
        return itemDetails.itemTypeId as ItemDetailsSchema[K];
      case 'description':
        return itemDetails.description as ItemDetailsSchema[K];
      case 'teamId':
        return itemDetails.teamId as ItemDetailsSchema[K];
      case 'targetStartDateTime':
        return itemDetails.targetStartDateTime as ItemDetailsSchema[K];
      case 'targetFinishDateTime':
        return itemDetails.targetFinishDateTime as ItemDetailsSchema[K];
      case 'estimationId':
        return itemDetails.estimationId as ItemDetailsSchema[K];
      case 'parentId':
        return itemDetails.parentId as ItemDetailsSchema[K];
      default: {
        const _exhaustiveCheck: never = field;
        return _exhaustiveCheck;
      }
    }
  };

  const updateFieldValue = async <K extends keyof ItemDetailsSchema>(
    _field: K,
    _value: ItemDetailsSchema[K],
  ): Promise<void> => {};

  const isEditable = (_field: keyof ItemDetailsSchema): boolean => {
    return false;
  };

  const isDisabled = (_field: keyof ItemDetailsSchema): boolean => {
    return false;
  };

  const isVisible = (_field: keyof ItemDetailsSchema): boolean => {
    return true;
  };

  const getFieldSchema = (): ItemDetailsSchema => {
    return {
      name: itemDetails.name,
      itemTypeId: itemDetails.itemTypeId,
      itemStatusId: itemDetails.statusId as number,
      parentId: itemDetails.parentId ?? undefined,
      description: itemDetails.description ?? undefined,
      teamId: itemDetails.teamId ?? undefined,
      targetStartDateTime: itemDetails.targetStartDateTime ?? undefined,
      targetFinishDateTime: itemDetails.targetFinishDateTime ?? undefined,
      estimationId: itemDetails.estimationId ?? undefined,
    };
  };

  const getItemTypeOption = () => {
    if (!enrichedItem.type) {
      return undefined;
    }
    return {
      value: itemDetails.itemTypeId,
      label: enrichedItem.type,
      icon: enrichedItem.itemTypeIcon,
      color: enrichedItem.itemTypeColor,
    };
  };

  const getStatusOption = () =>
    itemDetails.statusId && enrichedItem.statusName
      ? {
          value: itemDetails.statusId,
          label: enrichedItem.statusName,
          color: enrichedItem.statusColor,
        }
      : undefined;

  const getTeamOption = () =>
    itemDetails.teamId && enrichedItem.teamName
      ? {
          value: itemDetails.teamId,
          label: enrichedItem.teamName,
        }
      : undefined;

  const getParentItem = () =>
    itemDetails.parentId && enrichedItem.parentTitle
      ? {
          id: itemDetails.parentId,
          name: enrichedItem.parentTitle,
          itemTypeIcon: enrichedItem.parentItemTypeIcon,
          itemTypeColor: enrichedItem.parentItemTypeColor,
        }
      : undefined;

  const getEstimationOption = () =>
    itemDetails.estimationId && enrichedItem.estimationLabel
      ? {
          value: itemDetails.estimationId,
          label: enrichedItem.estimationLabel,
        }
      : undefined;

  return {
    getFieldValue,
    updateFieldValue,
    isEditable,
    isDisabled,
    isVisible,
    getFieldSchema,
    getItemTypeOption,
    getStatusOption,
    getTeamOption,
    getParentItem,
    getEstimationOption,
  };
};
